/**
 * Client-side copy of the consent contracts.
 *
 * server/api-util/aiConsent.js is the source of truth — it is what actually
 * enforces the gates. These constants exist because the browser bundle cannot
 * import from server/ without breaking the webpack build, and a paraphrased
 * notice would mean the person agreed to different words than the ones recorded
 * against their consent.
 *
 * src/util/aiConsent.test.js reads the server module and asserts the two agree,
 * purpose by purpose, so drift fails a test rather than shipping.
 */

/** Must match PURPOSES in server/api-util/aiConsent.js. */
export const PURPOSES = {
  VEHICLE_DESCRIPTION: 'vehicle-description',
  AI_CHAT: 'ai-chat',
  AI_CHAT_VOICE: 'ai-chat-voice',
  TRANSCRIPTION: 'transcription',
  SPEECH_RECOGNITION: 'speech-recognition',
  CRM_LEAD: 'crm-lead',
};

/** Must match CHAT_AI_PROVIDERS in server/api-util/aiConsent.js. */
export const CHAT_AI_PROVIDERS = ['DeepSeek', 'Google Gemini', 'Perplexity', 'OpenAI'];

/** Must match CONSENT_CONTRACTS in server/api-util/aiConsent.js. */
export const CONSENT_CONTRACTS = {
  [PURPOSES.VEHICLE_DESCRIPTION]: {
    version: '2026-08-09.ai-description.v1',
    text:
      'I agree to send the vehicle details entered here to OpenAI to generate a ' +
      'description. Do not include personal or sensitive information.',
  },
  [PURPOSES.AI_CHAT]: {
    version: '2026-08-26.ai-chat.v2',
    text:
      'This chat is answered by AI. Your messages go to our AI providers ' +
      `(${CHAT_AI_PROVIDERS.join(', ')}), who process them outside Canada. ` +
      'Please keep personal and sensitive details out of the chat — for anything ' +
      'private, contact the team directly instead.',
  },
  [PURPOSES.AI_CHAT_VOICE]: {
    version: '2026-08-09.ai-chat-voice.v1',
    text:
      'What you say is processed by AI services outside Canada to answer you. ' +
      'Press 1 to continue with the assistant, or press 2 to skip it and reach a ' +
      'human instead.',
  },
  [PURPOSES.TRANSCRIPTION]: {
    version: '2026-08-09.transcription.v1',
    text:
      'I agree to upload this audio recording to OpenAI Whisper to convert it ' +
      'to text. The recording is processed outside Canada. Do not record ' +
      'personal or sensitive information.',
  },
  [PURPOSES.SPEECH_RECOGNITION]: {
    version: '2026-08-09.speech-recognition.v1',
    text:
      'I agree to use my microphone for voice input. Your browser (Chrome uses ' +
      'Google, Safari uses Apple) sends the audio to its own speech service to ' +
      'convert it to text, outside Canada. Do not say anything sensitive.',
  },
  [PURPOSES.CRM_LEAD]: {
    version: '2026-08-09.crm-lead.v1',
    text:
      'I agree that SkipTheDealerships may store my name and contact details in ' +
      'its customer system (HubSpot) so the team can follow up with me.',
  },
};

export const contractFor = purpose => CONSENT_CONTRACTS[purpose];

/** Must match CANONICAL_LOCALE in server/api-util/aiConsent.js. */
export const CANONICAL_LOCALE = 'en';

/**
 * Locales with a reviewed, approved translation of each contract.
 *
 * Must match APPROVED_CONSENT_LOCALES on the server, and is deliberately
 * English-only: an unreviewed translation of a privacy contract is worse than
 * none, because the person agrees to a sentence nobody has checked while the
 * receipt records the canonical one.
 *
 * The consequence is intentional. In a locale that is not on this list the
 * feature is switched off rather than shown in English — see
 * `isConsentAvailable`. A privacy notice somebody cannot read is not a notice.
 */
export const APPROVED_CONSENT_LOCALES = Object.values(PURPOSES).reduce((acc, purpose) => {
  acc[purpose] = [CANONICAL_LOCALE];
  return acc;
}, {});

/** Normalises `fr-CA` / `FR` to the `fr` this registry is keyed by. */
export const normaliseLocale = locale =>
  typeof locale === 'string' && locale.trim()
    ? locale
        .trim()
        .toLowerCase()
        .split(/[-_]/)[0]
    : CANONICAL_LOCALE;

/**
 * Whether this purpose may be offered at all in this locale.
 *
 * Callers use this to decide whether to render the feature, not merely whether
 * to render the notice. Showing the button and refusing at the gate would be a
 * worse experience and no more honest.
 */
export const isConsentAvailable = (purpose, locale) => {
  const approved = APPROVED_CONSENT_LOCALES[purpose];
  return Boolean(approved) && approved.includes(normaliseLocale(locale));
};

/**
 * The consent parameters to attach to a request.
 *
 * A helper rather than two inline literals at each call site, because the pair
 * has to travel together — a flag without a version is refused by the server,
 * which is the correct outcome but an annoying way to find a typo.
 */
export const consentParams = purpose => ({
  aiConsent: true,
  consentVersion: contractFor(purpose).version,
});

// ── Remembering the answer ────────────────────────────────────────────────────

/**
 * Why the decision is remembered now, when it deliberately was not before.
 *
 * It used to live in component state, so it was asked again on every mount:
 * every page load, every navigation, every return visit. The original reasoning
 * was sound — re-reading a short notice costs a click, and getting consent
 * wrong costs somebody's typed words — but the result was a notice that a
 * regular visitor met dozens of times. Nobody reads a sentence they have
 * dismissed nine times. They learn where the button is and stop looking at the
 * words, which is the opposite of what a disclosure is for.
 *
 * Remembering is safe because of what the record contains. It stores the
 * decision, the contract version, and the locale the notice was read in, and it
 * only counts while the version and locale still match. Three consequences,
 * each of them the point:
 *
 *   - a reworded contract re-asks everybody automatically, because the stored
 *     version stops matching. That is what a version bump is for;
 *   - somebody who switches to a locale with no approved translation is not
 *     treated as having agreed to a sentence in a language they did not read;
 *   - a refusal is remembered too, so declining is a real answer rather than a
 *     question that returns on the next page.
 *
 * None of this weakens the gate. The server re-checks the version on every
 * single request and has no idea this storage exists; it only decides whether
 * the browser has to ask again.
 */
const STORAGE_PREFIX = 'skip_ai_consent.';

export const DECISIONS = { ACCEPTED: 'accepted', DECLINED: 'declined' };

/** localStorage throws in private modes and where storage is disabled. */
const safeStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/**
 * The stored decision, if it still applies to the contract in force.
 *
 * Fails closed on every uncertainty — no storage, unparseable JSON, a
 * superseded version, an unapproved locale — because each of those means we
 * cannot say which sentence this person read, and the only honest response to
 * that is to show it again.
 */
const storedDecision = (purpose, locale) => {
  const contract = contractFor(purpose);
  const storage = safeStorage();
  if (!contract || !storage) return null;

  try {
    const record = JSON.parse(storage.getItem(`${STORAGE_PREFIX}${purpose}`) || 'null');
    if (!record || record.version !== contract.version) return null;

    // With a locale supplied the person must still be in the one they read it
    // in; without one, trust the recorded locale. Either way it has to be
    // approved, or we are honouring a decision made against wording nobody
    // reviewed.
    const readIn = normaliseLocale(record.locale);
    if (locale !== undefined && normaliseLocale(locale) !== readIn) return null;
    if (!isConsentAvailable(purpose, readIn)) return null;

    return record.decision === DECISIONS.ACCEPTED || record.decision === DECISIONS.DECLINED
      ? record.decision
      : null;
  } catch (e) {
    return null;
  }
};

const remember = (purpose, locale, decision) => {
  const contract = contractFor(purpose);
  const storage = safeStorage();
  if (!contract || !storage) return;

  try {
    storage.setItem(
      `${STORAGE_PREFIX}${purpose}`,
      JSON.stringify({ decision, version: contract.version, locale: normaliseLocale(locale) })
    );
  } catch (e) {
    // Quota, private mode, or a browser that refuses. Being unable to remember
    // is a worse experience, not a broken one: the notice is shown again.
  }
};

/** Records that this person accepted this contract, in this locale. */
export const rememberConsent = (purpose, locale = CANONICAL_LOCALE) =>
  remember(purpose, locale, DECISIONS.ACCEPTED);

/**
 * Records a refusal.
 *
 * Stored under the same key as an acceptance, so a later "yes" replaces it
 * rather than leaving two records that could disagree.
 */
export const rememberDecline = (purpose, locale = CANONICAL_LOCALE) =>
  remember(purpose, locale, DECISIONS.DECLINED);

/** Whether a stored acceptance still stands. Never true for a refusal. */
export const hasRememberedConsent = (purpose, locale) =>
  storedDecision(purpose, locale) === DECISIONS.ACCEPTED;

/** Whether this person has already refused the contract in force. */
export const hasRememberedDecline = (purpose, locale) =>
  storedDecision(purpose, locale) === DECISIONS.DECLINED;

/** Clears the stored decision, so the notice is asked again from scratch. */
export const forgetConsent = purpose => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(`${STORAGE_PREFIX}${purpose}`);
  } catch (e) {
    // Nothing to do — it was never durable in the first place.
  }
};

// Named exports kept for the description flow, which reads them directly.
export const AI_CONSENT_VERSION = CONSENT_CONTRACTS[PURPOSES.VEHICLE_DESCRIPTION].version;
export const AI_CONSENT_TEXT = CONSENT_CONTRACTS[PURPOSES.VEHICLE_DESCRIPTION].text;
export const CHAT_CONSENT_VERSION = CONSENT_CONTRACTS[PURPOSES.AI_CHAT].version;
export const CHAT_CONSENT_TEXT = CONSENT_CONTRACTS[PURPOSES.AI_CHAT].text;
export const TRANSCRIPTION_CONSENT_VERSION = CONSENT_CONTRACTS[PURPOSES.TRANSCRIPTION].version;
export const TRANSCRIPTION_CONSENT_TEXT = CONSENT_CONTRACTS[PURPOSES.TRANSCRIPTION].text;
export const SPEECH_CONSENT_VERSION = CONSENT_CONTRACTS[PURPOSES.SPEECH_RECOGNITION].version;
export const CRM_CONSENT_VERSION = CONSENT_CONTRACTS[PURPOSES.CRM_LEAD].version;
export const CRM_CONSENT_TEXT = CONSENT_CONTRACTS[PURPOSES.CRM_LEAD].text;
